import * as React from 'react';
import { useMutation, MutationHookOptions } from '@apollo/react-hooks';
import { unionBy } from 'lodash';
import { DataProxy } from 'apollo-cache';

import { message } from 'antd';

import { SAVE_APPLICATION_MUTATION, GET_APPLICATIONS_QUERY } from '@frontend/app/queries';
import { GetApplicationsQuery } from '@frontend/app/queries/types/GetApplicationsQuery';
import {
  SaveApplicationMutation,
  SaveApplicationMutationVariables,
} from '@frontend/app/queries/types/SaveApplicationMutation';
import { IApplication } from '.';

const { useEffect } = React;

const updateCache = (store: DataProxy, application: IApplication) => {
  let data: GetApplicationsQuery;
  try {
    data = store.readQuery<GetApplicationsQuery>({ query: GET_APPLICATIONS_QUERY });
  } catch (err) {
    console.log(`cache missing: ${GET_APPLICATIONS_QUERY}`);
    return;
  }

  // Write our data back to the cache.
  store.writeQuery<GetApplicationsQuery>({
    query: GET_APPLICATIONS_QUERY,
    data: {
      ...data,
      applications: unionBy(data.applications, [application], 'id'),
    },
  });
};

type IOptions = MutationHookOptions<SaveApplicationMutation, SaveApplicationMutationVariables>;

export function useSaveApplication(options: IOptions = {}) {
  const [saveApplication, { loading, error }] = useMutation<SaveApplicationMutation, SaveApplicationMutationVariables>(
    SAVE_APPLICATION_MUTATION,
    {
      ...options,
      update(...args) {
        if (options.update) {
          options.update(...args);
        }

        const [store, result] = args;

        updateCache(store, result.data.application);
      },
      onCompleted() {
        message.info('Successfully saved application');
      },
    },
  );

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  return {
    saveApplication,
    loading,
    error,
  };
}
